// Groups the header's "explore" destinations (submenuItems) into the mega-menu
// categories from Marin's design (DoD 22). Everything here is DATA-DRIVEN: a
// category only renders when the current role/perspective actually has items in
// it, so the layout adapts to the item count (no hard-coded A/B variant).
//
// The categorisation is by route path so it stays correct regardless of the
// runtime-translated label. Items whose path isn't mapped fall into a trailing
// "More" bucket rather than disappearing.
// Canonical column order (hotfix D30): Marketplace · Scouting · Tools · Network
// · Content. Marketplace always leads. Only non-empty categories render, so the
// agent perspective (no /scouting/* items) simply drops the Scouting column and
// still starts at Marketplace; club roles show Scouting in second place.
const CATEGORY_DEFS = [
  // Re-sort (Marin 2026-08-13): Discovery is a scouting TOOL, not a demand
  // stream — it moved from Marketplace to Tools. Friendlies is club-to-club
  // NETWORKING — it moved from Marketplace (where D-006 had parked it) to
  // Network, next to Events.
  { id: "marketplace", label: "Marketplace", paths: ["/agent-tenders", "/scout-requests", "/last-minute", "/network/agent-requests", "/network/trusted-agent", "/network/search-requests/inbox", "/market"] },
  // Scout Requests (club side, design 4a): its own SCOUTING group holding Scout
  // Requests + Radar. Renders only when the role actually has those items (club
  // roles); the agent's Scout Requests lives under Marketplace (design 3a).
  { id: "scouting", label: "Scouting", paths: ["/scouting/requests", "/scouting/radar"] },
  { id: "tools", label: "Tools", paths: ["/agency/taskboard", "/agent/radar", "/discovery", "/compare", "/team-of-the-week"] },
  { id: "network", label: "Network", paths: ["/lounge", "/organizers", "/lawyers", "/friendlies", "/events"] },
  { id: "content", label: "Content", paths: ["/whats-new", "/news", "/competitions"] },
];

// Team of the Week sits in TOOLS by default (CATEGORY_DEFS above), but the
// CLUB perspective files it under SCOUTING — see buildMegaCategories.
const TOTW_PATH = "/team-of-the-week";

// Build ordered, non-empty categories from a role's submenuItems.
// Each item keeps its { icon, title, description, path } shape.
//
// options.totwColumn — role-aware TOTW placement (Marin 2026-08-14):
//   "scouting"  club perspective (ClubAdmin, or Staff/Coach WITH a club —
//               the caller decides via profileData.club_details): the tile
//               moves next to Scout Requests + Radar.
//   "tools"     (default) agents, staff WITHOUT a club, everyone else.
// Claim-once stays intact: the path is moved between the two defs, never
// duplicated — one tile per render, in exactly one column. And the move only
// happens when the role actually HAS /scouting/* items, so no role can end
// up with a Scouting column that exists only for TOTW (agents keep their
// column set unchanged, a non-KYC club member falls back to Tools).
export const buildMegaCategories = (submenuItems = [], { totwColumn = "tools" } = {}) => {
  const scoutingDef = CATEGORY_DEFS.find((d) => d.id === "scouting");
  const moveTotwToScouting =
    totwColumn === "scouting" &&
    submenuItems.some((it) => scoutingDef.paths.includes(it.path));
  const defs = moveTotwToScouting
    ? CATEGORY_DEFS.map((def) => {
        if (def.id === "scouting") return { ...def, paths: [...def.paths, TOTW_PATH] };
        if (def.id === "tools") return { ...def, paths: def.paths.filter((p) => p !== TOTW_PATH) };
        return def;
      })
    : CATEGORY_DEFS;

  const claimed = new Set();
  const cats = defs.map((def) => {
    const items = submenuItems.filter((it) => def.paths.includes(it.path));
    items.forEach((it) => claimed.add(it.path));
    return { id: def.id, label: def.label, items };
  }).filter((c) => c.items.length > 0);

  // Anything not mapped above still shows, under a "More" category.
  const leftovers = submenuItems.filter((it) => !claimed.has(it.path));
  if (leftovers.length > 0) {
    cats.push({ id: "more", label: "More", items: leftovers });
  }
  return cats;
};
